import { CheckCircleFilledIcon, LocationIcon } from '@velocity/icons/system';
import { LinkInline, PromoLabel, RadiobuttonCard } from '@velocity/ui';
import React from 'react';
import { Dealer } from '../../../types/instant-lease-api';

export interface DealerCardProps {
  dealer: Dealer;
  /** Distance to the customer address in km, `null` when unknown */
  distance: number | null;
  checked: boolean;
  /** Marks the nearest dealer (Figma: promo-label "Empfehlung") */
  recommended?: boolean;
  name: string;
  value: string;
  onSelect: (dealer: Dealer) => void;
  onOpenMap: (dealer: Dealer) => void;
}

const formatDistance = (distance: number): string =>
  `${distance.toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} km`;

/*
 * Dealer option (Figma: Instant Lease - B2C, nodes 492:8545 / 495:13962 /
 * 497:15259 -> "formkit/radiobutton-card" + promo-labels + "Auf der Karte").
 *
 * The card itself is the Velocity `RadiobuttonCard` (white, 1px neutral-300,
 * 4px radius, 16px padding, title text-200-bold, details text-100). The meta
 * block on the right (labels + map link) is not part of the Velocity API, so
 * it is layered on top, 8px from the card's top/right edge like in Figma.
 *
 *   tablet / desktop   [Empfehlung ✓] [⌖ 1,2 km]   inside the card, top-right
 *   mobile             [Empfehlung ✓] above the card, [⌖ 1,2 km] top-right
 */
const DealerCard: React.FC<DealerCardProps> = ({
  dealer,
  distance,
  checked,
  recommended = false,
  name,
  value,
  onSelect,
  onOpenMap,
}) => {
  const hasDistance = distance !== null;
  const hasMeta = hasDistance || recommended;

  // Reserve space for the overlay so title / address never run under it.
  const contentPaddingRight = !hasMeta
    ? ''
    : recommended && hasDistance
      ? 'pr-[96px]! md:pr-[200px]!'
      : recommended
        ? 'pr-5! md:pr-[136px]!'
        : 'pr-[96px]!';

  return (
    <div className='flex w-full flex-col' data-testid='dealer-card'>
      {recommended && (
        <PromoLabel
          kind='primary'
          Icon={CheckCircleFilledIcon}
          alignment='end'
          className='md:hidden!'
          data-testid='dealer-recommended-mobile'
        >
          Empfehlung
        </PromoLabel>
      )}

      <div className='relative w-full'>
        <RadiobuttonCard
          name={name}
          value={value}
          checked={checked}
          onChange={() => onSelect(dealer)}
          title={dealer.name1}
          details={
            <>
              {dealer.street}
              <br />
              {dealer.zip_code} {dealer.city}
            </>
          }
          className={`w-full! px-5! py-4! ${contentPaddingRight}`}
          data-testid='dealer-radio-card'
        />

        {hasMeta && (
          <div className='absolute top-2 right-2 flex flex-col items-end gap-2'>
            <div className='flex items-start gap-1'>
              {recommended && (
                <PromoLabel
                  kind='primary'
                  Icon={CheckCircleFilledIcon}
                  alignment='end'
                  className='max-md:hidden!'
                  data-testid='dealer-recommended'
                >
                  Empfehlung
                </PromoLabel>
              )}
              {hasDistance && (
                <PromoLabel
                  kind='secondary'
                  Icon={LocationIcon}
                  data-testid='dealer-distance'
                >
                  {formatDistance(distance)}
                </PromoLabel>
              )}
            </div>

            <span className='text-tight-100'>
              <LinkInline
                component='button'
                emphasis='low'
                className='text-(--vel-color-secondary-400)!'
                onClick={() => onOpenMap(dealer)}
                data-testid='dealer-open-map'
              >
                Auf der Karte
              </LinkInline>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealerCard;
